import type { Dispatch, SetStateAction } from "react";

import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/common/Dialog";
import { X } from "@phosphor-icons/react/dist/ssr";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Typography from "@/shared/components/common/Typography";
import Input from "@/shared/components/common/input/Input";
import Skeleton from "@/shared/components/common/Skeleton";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  type LoginByPasswordSchema,
  loginByPasswordSchema,
} from "@/shared/schemas/profile/login.password.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/common/form";

import data from "@/public/data/landing.json";
import { useForm } from "react-hook-form";

interface LoginSignupWithsProps {
  onSubmit: ({
    mobile,
    password,
  }: {
    mobile: number;
    password: string;
  }) => void;

  loading: boolean;
  setStep: Dispatch<
    SetStateAction<"login" | "confirm" | "loginWithPassword" | null>
  >;
}

export default function LoginWithPassword({
  onSubmit,
  loading,

  setStep,
}: LoginSignupWithsProps) {
  const form = useForm({
    defaultValues: { mobile: undefined, password: "" },
    resolver: yupResolver(loginByPasswordSchema),
  });

  const { control, handleSubmit } = form;

  const {
    header: {
      loginSignupModal: {
        loginWithPassword: { description, btn, btnCode, title, inputs },
      },
    },
  } = data;

  return (
    <Flex direction={"col"} gap={"2"}>
      <DialogHeader className="flex flex-col  items-start mb-3 gap-3">
        {/* Title & Description */}

        <DialogTitle className="mx-auto font-semibold leading-relaxed">
          {title}
        </DialogTitle>
        <DialogDescription className="mx-auto">
          <Typography className="font-medium">{description}</Typography>
        </DialogDescription>
      </DialogHeader>

      {/* Inputs */}
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction={"col"} gap={"6"}>
            {inputs.map(({ name, placeHolder, type }) => (
              <FormField
                key={`${name}-${type}`}
                control={control}
                name={name as keyof LoginByPasswordSchema}
                render={({ field }) => (
                  <FormItem className="!pt-3 relative">
                    <FormLabel
                      value={!!field.value}
                      className="text-muted-foreground"
                    >
                      {placeHolder}
                    </FormLabel>
                    <FormControl>
                      {/* @ts-ignore */}
                      <Input
                        {...field}
                        type={type}
                        className="!h-12 rounded-xs! text-foreground"
                      />
                    </FormControl>
                    <FormMessage className="absolute -bottom-[20px]! right-3 text-xs" />
                  </FormItem>
                )}
              />
            ))}

            {/* Submit button */}
            <DialogFooter>
              {loading ? (
                <Skeleton className="w-full py-5 rounded-xs" />
              ) : (
                <Flex className="flex-col w-full gap-2">
                  <Button
                    variant={"default"}
                    type="submit"
                    className="w-full rounded-full"
                  >
                    {btn}
                  </Button>
                  <Button
                    onClick={() => setStep("login")}
                    type="button"
                    variant="ghost"
                    className="w-full rounded-full text-primary! hover:bg-muted dark:hover:bg-muted/10"
                  >
                    {btnCode}
                  </Button>
                </Flex>
              )}
            </DialogFooter>
          </Flex>
        </form>
      </Form>
    </Flex>
  );
}
